/**
 * planner/models.ts — types for Module #5, the Roamer Planner™.
 *
 * A platform MODULE, not a core engine. The strategic planning center:
 * travel plans, lifestyle planning, goals, a living timeline, decision
 * support, and future-scenario comparison. NOT a calendar — an intelligent
 * planning assistant. Honesty rules as everywhere: unknown ⇒ null, and
 * every suggestion/verdict carries the evidence it was derived from.
 */

// ─── Destinations & travel plan ────────────────────────────────────────────────

export type DestinationStatus = 'next' | 'future' | 'dream';

export interface PlannedDestination {
  id: string;
  city: string;
  country: string;
  status: DestinationStatus;
  /** Planned dates; null when flexible/undecided. */
  plannedArrival: number | null;
  plannedDeparture: number | null;
  flexibleDates: boolean;
  notes: string;
  addedAt: number;
}

export interface TravelPlanState {
  /** From the Location Engine via the Core bus; null until known. */
  currentBaseCity: string | null;
  currentBaseCountry: string | null;
  nextDestination: PlannedDestination | null;
  futureDestinations: PlannedDestination[];
  dreamDestinations: PlannedDestination[];
}

// ─── Lifestyle plan ────────────────────────────────────────────────────────────

export interface LifestylePlan {
  /** Where the user wants to live next; free-form, null if undecided. */
  whereToLive: string | null;
  /** Months to stay at the current/next base; null if open. */
  stayDurationMonths: number | null;
  monthlyBudget: number | null;
  budgetCurrency: string;
  monthlyIncomeGoal: number | null;
  /** Free-form timeline notes — the user's own words, never invented. */
  housingTimelineNote: string | null;
  visaTimelineNote: string | null;
  /** Missions per week the user plans to run; null if unplanned. */
  missionsPerWeek: number | null;
  coworkingDaysPerWeek: number | null;
  restDaysPerWeek: number | null;
  explorationDaysPerWeek: number | null;
}

export const DEFAULT_LIFESTYLE_PLAN: LifestylePlan = {
  whereToLive: null,
  stayDurationMonths: null,
  monthlyBudget: null,
  budgetCurrency: 'USD',
  monthlyIncomeGoal: null,
  housingTimelineNote: null,
  visaTimelineNote: null,
  missionsPerWeek: null,
  coworkingDaysPerWeek: null,
  restDaysPerWeek: null,
  explorationDaysPerWeek: null,
};

// ─── Goals ─────────────────────────────────────────────────────────────────────

export const PLANNER_GOAL_TYPES = [
  'income',
  'savings',
  'travel',
  'xp',
  'mission',
  'country',
  'city',
  'personal',
] as const;
export type PlannerGoalType = (typeof PLANNER_GOAL_TYPES)[number];

/** income / savings / mission auto-track from Core bus traffic; others are manual. */
export const AUTO_TRACKED_GOAL_TYPES: PlannerGoalType[] = ['income', 'savings', 'mission'];

export interface PlannerGoal {
  id: string;
  type: PlannerGoalType;
  title: string;
  targetAmount: number;
  currentAmount: number;
  /** Optional deadline; null = open-ended. */
  targetDate: number | null;
  createdAt: number;
  completedAt: number | null;
}

// ─── Smart suggestions ─────────────────────────────────────────────────────────

export interface PlannerSuggestion {
  /** Deterministic — stable across recomputation while the condition holds. */
  id: string;
  message: string;
  /** The recorded data the suggestion was derived from. Never empty. */
  basis: string[];
  severity: 'info' | 'nudge' | 'important';
}

// ─── Timeline (living roadmap — computed at read time, so always current) ─────

export const TIMELINE_HORIZONS = [
  'today',
  'thisWeek',
  'thisMonth',
  'nextMonth',
  'threeMonths',
  'sixMonths',
  'oneYear',
] as const;
export type TimelineHorizon = (typeof TIMELINE_HORIZONS)[number];

export interface TimelineMilestone {
  id: string;
  horizon: TimelineHorizon;
  at: number | null; // null for open-ended goals placed by deadline-less heuristics
  label: string;
  kind: 'travel' | 'goal' | 'lifestyle';
}

export type PlannerTimeline = Record<TimelineHorizon, TimelineMilestone[]>;

// ─── Decision support ──────────────────────────────────────────────────────────

export const PLANNER_QUESTIONS = [
  'shouldIStay',
  'shouldIMove',
  'shouldIRenewVisa',
  'shouldIChangeApartments',
  'shouldIChangeCountries',
  'shouldISaveMoreFirst',
  'shouldIWaitAnotherMonth',
] as const;
export type PlannerQuestion = (typeof PLANNER_QUESTIONS)[number];

export interface DecisionSupport {
  question: PlannerQuestion;
  /** Never a fake certainty: with thin data the verdict is 'insufficientData'. */
  verdict: 'leanYes' | 'leanNo' | 'balanced' | 'insufficientData';
  considerations: { text: string; direction: 'for' | 'against'; basis: string }[];
  disclaimer: string;
}

export const DECISION_DISCLAIMER =
  'Decision support weighs only the data you have recorded — it is guidance, not a directive.';

// ─── Compare futures (scenario planning) ───────────────────────────────────────

/** All metrics user/engine-recorded and nullable — nothing is invented. */
export interface FutureScenario {
  id: string;
  name: string;
  city: string | null;
  monthlyBudget: number | null;
  roamerScore: number | null;
  projectedMonthlySavings: number | null;
  incomePotential: number | null;
  housingNote: string | null;
  communityNote: string | null;
  weatherNote: string | null;
  qualityOfLifeNote: string | null;
  createdAt: number;
}

export interface FutureComparisonRow {
  label: string;
  values: (string | number | null)[];
}

export interface FutureComparison {
  scenarioIds: string[];
  names: string[];
  rows: FutureComparisonRow[];
  /** Named winner per numeric row where BOTH sides have data; null otherwise. */
  edges: { label: string; winner: string | null }[];
}

// ─── AI-assisted planning (future — interface only) ───────────────────────────

export interface PlannerAiAssistant {
  id: string;
  name: string;
  buildPlan(prompt: string): Promise<PlannerSuggestion[]>;
}

// ─── Export ────────────────────────────────────────────────────────────────────

export interface PlannerExport {
  exportedAt: number;
  travelPlan: TravelPlanState;
  lifestylePlan: LifestylePlan;
  goals: PlannerGoal[];
  scenarios: FutureScenario[];
}
